import { useEffect, useState, useRef } from 'react';
import { Gig } from 'shared/types/appTypes';
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  getDocs,
  getDoc,
  doc,
  setDoc,
  Timestamp,
  arrayUnion
} from 'firebase/firestore';
import { db, functions } from 'src/firebase/firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { useSession } from 'src/SessionContext';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  Container,
  Divider,
  useTheme,
  useMediaQuery,
  IconButton,
  Popper,
  Paper,
  ClickAwayListener,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Badge,
  Tooltip,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import FilterListIcon from '@mui/icons-material/FilterList';
import { gigStatuses, statusesWithAnyDetails } from 'src/constants/appConstants';
import ButtonsSwitch from 'src/pages/talent-view/components/ButtonsSwitch';
import GigDetails from './GigDetails'; // Import the new component
import { stripBlackWhiteColors } from 'src/utils/htmlColorUtils';

interface TalentViewProps {
  talentId: string;
}

const TalentView: React.FC<TalentViewProps> = ({ talentId }) => {
  const [gigs, setGigs] = useState<Gig[]>([]);
  const [filteredGigs, setFilteredGigs] = useState<Gig[]>([]);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [showOlderEvents, setShowOlderEvents] = useState<boolean>(false);
  const [smsStatus, setSmsStatus] = useState<any>(null);

  // Confirmation dialog state
  const [confirmationDialog, setConfirmationDialog] = useState<{
    open: boolean;
    content: string;
    gig: Gig | null;
  }>({
    open: false,
    content: '',
    gig: null
  });

  // Scroll tracking state
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState<boolean>(false);
  const [scrollProgress, setScrollProgress] = useState<number>(0);
  const contentRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isFilterOpen = Boolean(filterAnchorEl);
  const { session } = useSession();

  // Filter out "Pre-inquiry" status
  const availableStatuses = gigStatuses.filter((status) => status !== 'Pre-inquiry');

  useEffect(() => {
    // Calculate date from one week ago (or all events if showOlderEvents is true)
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    // Calculate date from 6 months ago for the older events cutoff
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    // Format the dates as YYYY-MM-DD for comparison
    const oneWeekAgoStr = oneWeekAgo.toISOString().split('T')[0];
    const sixMonthsAgoStr = sixMonthsAgo.toISOString().split('T')[0];

    // Create query for gigs collection
    const gigsRef = collection(db, 'gigs');

    let gigsQuery;
    if (showOlderEvents) {
      // Show events from 6 months ago forward when showOlderEvents is true
      gigsQuery = query(
        gigsRef,
        where('talentId', '==', talentId),
        where('status', 'not-in', ['Pre-inquiry', 'Assigned']),
        where('eventDate', '>=', sixMonthsAgoStr),
        orderBy('eventDate')
      );
    } else {
      // Show events from one week ago forward
      gigsQuery = query(
        gigsRef,
        where('talentId', '==', talentId),
        where('status', 'not-in', ['Pre-inquiry', 'Assigned']),
        where('eventDate', '>=', oneWeekAgoStr),
        orderBy('eventDate')
      );
    }

    // Set up the snapshot listener
    const unsubscribe = onSnapshot(gigsQuery, (snapshot) => {
      const gigsData: Gig[] = [];
      snapshot.forEach((doc) => {
        gigsData.push({ id: doc.id, ...doc.data() } as Gig);
      });
      setGigs(gigsData);
      setFilteredGigs(gigsData);
    });

    // Clean up listener when component unmounts
    return () => unsubscribe();
  }, [talentId, showOlderEvents]);

  // Apply filters whenever selected statuses change or gigs data changes
  useEffect(() => {
    if (selectedStatuses.length === 0) {
      setFilteredGigs(gigs);
    } else {
      const filtered = gigs.filter((gig) => gig.status && selectedStatuses.includes(gig.status));
      setFilteredGigs(filtered);
    }
  }, [selectedStatuses, gigs]);

  // Calculate status counts when gigs data changes
  useEffect(() => {
    const counts: Record<string, number> = {};
    gigs.forEach((gig) => {
      if (gig.status) {
        counts[gig.status] = (counts[gig.status] || 0) + 1;
      }
    });
    setStatusCounts(counts);
  }, [gigs]);

  // Check SMS status for the talent with caching
  useEffect(() => {
    if (!talentId) return;

    const checkSMSStatus = async () => {
      try {
        // First check if we need to update based on lastPhoneCheck
        const accessQuery = query(collection(db, 'access'), where('self', '==', `/v1/contacts/${talentId}`));
        const accessSnapshot = await getDocs(accessQuery);

        let shouldCallAPI = true;
        let accessDocRef = null;

        if (!accessSnapshot.empty) {
          const accessDoc = accessSnapshot.docs[0];
          accessDocRef = accessDoc.ref;
          const accessData = accessDoc.data();
          const lastPhoneCheck = accessData.lastPhoneCheck?.toDate();

          if (lastPhoneCheck) {
            const threeDaysAgo = new Date();
            threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);

            if (lastPhoneCheck > threeDaysAgo) {
              shouldCallAPI = false;

              // Use cached SMS status if available
              if (accessData.cachedSMSStatus) {
                setSmsStatus(accessData.cachedSMSStatus);
                return;
              }
            }
          }
        }

        if (shouldCallAPI) {
          const checkSMSStatusFn = httpsCallable(functions, 'checkSMSStatus');
          const result = await checkSMSStatusFn({ contactIds: [talentId] });

          // Handle the nested response structure
          const smsData = (result.data as any)?.data || result.data;
          setSmsStatus(smsData);

          // Cache the result and update lastPhoneCheck
          if (accessDocRef) {
            await setDoc(
              accessDocRef,
              {
                lastPhoneCheck: Timestamp.now(),
                cachedSMSStatus: smsData
              },
              { merge: true }
            );
          } else {
            // Create new access document if it doesn't exist
            const newAccessDocRef = doc(collection(db, 'access'));
            await setDoc(newAccessDocRef, {
              self: `/v1/contacts/${talentId}`,
              lastPhoneCheck: Timestamp.now(),
              cachedSMSStatus: smsData
            });
            console.log('Successfully created new access document with cache data');
          }
        }
      } catch (error) {
        console.error('Error checking SMS status:', error);
        setSmsStatus(null);
      }
    };

    checkSMSStatus();
  }, [talentId]);

  // Function to opt in talent to SMS
  const handleOptInToSMS = async () => {
    if (!smsStatus?.smsStatusResults?.[0]?.phone) return;

    try {
      const phoneDigits = smsStatus.smsStatusResults[0].phone.replace(/\D/g, '');

      const optInDocRef = doc(db, 'optIn', phoneDigits);
      await setDoc(optInDocRef, {
        enabled: true,
        timestamp: Timestamp.now()
      });

      // Clear the cached SMS status so next check gets fresh data
      const accessQuery = query(collection(db, 'access'), where('self', '==', `/v1/contacts/${talentId}`));
      const accessSnapshot = await getDocs(accessQuery);

      if (!accessSnapshot.empty) {
        const accessDoc = accessSnapshot.docs[0];
        await setDoc(
          accessDoc.ref,
          {
            lastPhoneCheck: null,
            cachedSMSStatus: null
          },
          { merge: true }
        );
      }

      // Refresh SMS status to reflect the change
      const checkSMSStatusFn = httpsCallable(functions, 'checkSMSStatus');
      const result = await checkSMSStatusFn({ contactIds: [talentId] });
      const smsData = (result.data as any)?.data || result.data;
      setSmsStatus(smsData);

      // Update cache with new data
      if (!accessSnapshot.empty) {
        const accessDoc = accessSnapshot.docs[0];
        await setDoc(
          accessDoc.ref,
          {
            lastPhoneCheck: Timestamp.now(),
            cachedSMSStatus: smsData
          },
          { merge: true }
        );
      }
    } catch (error) {
      console.error('Error opting in to SMS:', error);
    }
  };

  // Check if we should show the opt-in button
  const shouldShowOptInButton = () => {
    const smsResult = smsStatus?.smsStatusResults?.[0];
    const shouldShow =
      smsResult && smsResult.phone && smsResult.optInStatus === 'not-opted-in' && !smsResult.canReceiveSMS;

    return shouldShow;
  };

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(filterAnchorEl ? null : event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleStatusChange = (status: string) => {
    setSelectedStatuses((prev) => {
      if (prev.includes(status)) {
        return prev.filter((s) => s !== status);
      } else {
        return [...prev, status];
      }
    });
  };

  const clearAllFilters = () => {
    setSelectedStatuses([]);
  };

  // Helper function to get chip color based on status
  const getStatusColor = (status?: string) => {
    if (!status) return 'default';

    switch (status.toLowerCase()) {
      case 'confirmed':
        return 'success';
      case 'availability check':
        return 'warning';
      case 'available':
        return 'success';
      case 'confirm details':
        return 'warning';
      case 'reconfirm details':
        return 'warning';
      case 'booked':
        return 'success';
      case 'unavailable':
        return 'error';
      case 'cancelled':
        return 'error';
      case 'offered':
        return 'warning';
      case 'offered (travel)':
        return 'warning';
      default:
        return 'info';
    }
  };

  // Format date for display
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'No date';

    const date = new Date(dateString.replace(/-/g, '\/'));
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  // Fetch confirmation content from Firebase template
  const fetchConfirmationContent = async (status: string): Promise<string> => {
    try {
      const templateQuery = query(collection(db, 'templateEmails'), where('name', '==', status));
      const templateSnapshot = await getDocs(templateQuery);

      if (!templateSnapshot.empty) {
        const templateDoc = templateSnapshot.docs[0];
        const templateData = templateDoc.data();
        return templateData.confirmation || '';
      }
      return '';
    } catch (error) {
      console.error('Error fetching confirmation content:', error);
      return '';
    }
  };

  // Handle showing confirmation dialog for offered statuses
  const handleShowConfirmation = async (gig: Gig) => {
    if (!gig.status) return;
    const confirmationContent = await fetchConfirmationContent(gig.status);

    setConfirmationDialog({
      open: true,
      content: confirmationContent,
      gig: gig
    });

    // Reset scroll tracking
    setHasScrolledToBottom(false);
    setScrollProgress(0);
  };

  // Handle scroll in dialog content
  const handleDialogScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const element = event.currentTarget;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;

    // Calculate scroll progress
    const maxScroll = scrollHeight - clientHeight;
    const progress = maxScroll > 0 ? (scrollTop / maxScroll) * 100 : 100;
    setScrollProgress(progress);

    // Check if user has scrolled to bottom (with 5px tolerance)
    if (scrollTop + clientHeight >= scrollHeight - 5) {
      setHasScrolledToBottom(true);
    }
  };

  // Check if user can proceed (has scrolled to bottom)
  const canProceed = hasScrolledToBottom;

  // Handle confirmation dialog close
  const handleConfirmationClose = () => {
    setConfirmationDialog({
      open: false,
      content: '',
      gig: null
    });

    // Reset tracking state
    setHasScrolledToBottom(false);
    setScrollProgress(0);
  };

  // Handle status update from confirmation dialog
  const handleConfirmAvailable = async () => {
    if (!confirmationDialog.gig?.id) return;

    try {
      if (!confirmationDialog.gig.eventId) {
        throw new Error('This gig is missing its event link. Please contact the office.');
      }

      const eventDoc = await getDoc(doc(db, 'events', confirmationDialog.gig.eventId));
      const canonicalEventDate = eventDoc.exists() ? eventDoc.data()?.dateISO8601 : null;

      if (!canonicalEventDate) {
        throw new Error('This event is missing its date. Please contact the office.');
      }

      if (confirmationDialog.gig.eventDate !== canonicalEventDate) {
        const repairEventGigDates = httpsCallable(functions, 'repairEventGigDates');
        await repairEventGigDates({ eventId: confirmationDialog.gig.eventId });
        throw new Error('This gig date was out of sync and is being repaired. Please refresh before responding.');
      }

      const user = session?.user?.name;

      await setDoc(
        doc(db, 'gigs', confirmationDialog.gig.id),
        {
          status: 'Booked',
          updatedAt: Timestamp.now(),
          updatedBy: user || 'Unknown',
          statusTimestamps: arrayUnion({
            changedTo: 'Booked',
            changedAt: Timestamp.now(),
            changedBy: user || 'Unknown'
          })
        },
        { merge: true }
      );

      handleConfirmationClose();
      // Note: We could add a notification here, but the real-time listener will update the UI
    } catch (error) {
      console.error('Error updating status:', error);
      handleConfirmationClose();
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* SMS Opt-in Banner */}
      {shouldShowOptInButton() && (
        <Box
          sx={{
            mb: 3,
            p: 2,
            border: 2,
            borderColor: 'warning.main',
            borderRadius: 1,
            backgroundColor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box>
            <Typography variant="body1" color="text.primary">
              <strong>Enable SMS Notifications</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We'd like to send you SMS updates for your gigs at {smsStatus?.smsStatusResults?.[0]?.phone}
            </Typography>
          </Box>
          <Button variant="contained" color="primary" onClick={handleOptInToSMS} sx={{ ml: 2, flexShrink: 0 }}>
            Enable SMS
          </Button>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Recent and Upcoming Gigs</Typography>

        <Tooltip title="Filter by status">
          <Badge
            color="primary"
            badgeContent={selectedStatuses.length}
            sx={{ '& .MuiBadge-badge': { fontSize: '0.8rem' } }}
          >
            <IconButton
              onClick={handleFilterClick}
              color={isFilterOpen || selectedStatuses.length > 0 ? 'primary' : 'default'}
              aria-label="filter gigs"
            >
              <FilterListIcon />
            </IconButton>
          </Badge>
        </Tooltip>

        <Popper open={isFilterOpen} anchorEl={filterAnchorEl} placement="bottom-end" style={{ zIndex: 1300 }}>
          <ClickAwayListener onClickAway={handleFilterClose}>
            <Paper elevation={3} sx={{ p: 2, minWidth: 200, maxWidth: 300 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                Filter by Status
              </Typography>
              <Divider sx={{ mb: 1 }} />

              <FormGroup>
                {availableStatuses.map((status) => (
                  <FormControlLabel
                    key={status}
                    control={
                      <Checkbox
                        checked={selectedStatuses.includes(status)}
                        onChange={() => handleStatusChange(status)}
                        size="small"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2">{status}</Typography>
                        <Box
                          component="span"
                          ml={1}
                          sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          <Chip
                            label=""
                            size="small"
                            color={getStatusColor(status) as any}
                            sx={{ width: 20, height: 20, '& .MuiChip-label': { p: 0 } }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              position: 'absolute',
                              fontWeight: 'bold',
                              fontSize: '0.65rem',
                              color: theme.palette.getContrastText(
                                (
                                  theme.palette[
                                    getStatusColor(status) as 'success' | 'info' | 'warning' | 'error'
                                  ] as any
                                )?.main
                              )
                            }}
                          >
                            {statusCounts[status] || 0}
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                ))}
              </FormGroup>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Button size="small" onClick={clearAllFilters} disabled={selectedStatuses.length === 0}>
                  Clear All
                </Button>
                <Button size="small" variant="contained" onClick={handleFilterClose}>
                  Close
                </Button>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant={showOlderEvents ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setShowOlderEvents(!showOlderEvents)}
                  fullWidth
                >
                  {showOlderEvents ? 'Hide Older Events' : 'Show Older Events'}
                </Button>
              </Box>
            </Paper>
          </ClickAwayListener>
        </Popper>
      </Box>

      {filteredGigs.length === 0 ? (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="body1" color="text.secondary" align="center">
              {gigs.length === 0 ? 'No upcoming gigs found.' : 'No gigs match your filter criteria.'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ mt: 2 }}>
          {filteredGigs.map((gig) => {
            const showDetails = gig.status && statusesWithAnyDetails.includes(gig.status);

            return (
              <Card key={gig.id} sx={{ mb: 2, boxShadow: 2 }}>
                <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          flexWrap: 'wrap',
                          mb: 1
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: isMobile ? 1 : 0 }}>
                          <EventIcon color="primary" sx={{ mr: 1 }} />
                          <Typography variant="subtitle1" fontWeight="bold">
                            {formatDate(gig.eventDate)}
                          </Typography>
                        </Box>
                        <Chip
                          label={gig.status || 'Unknown'}
                          color={getStatusColor(gig.status) as any}
                          size={isMobile ? 'small' : 'medium'}
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="h6" component="h2" gutterBottom>
                        {gig.eventFriendlyTitle || 'Untitled Event'}
                      </Typography>
                      <Divider sx={{ my: 1 }} />
                    </Grid>

                    {gig.status === 'Check General Availability' && (
                      <Grid item xs={12}>
                        <Typography sx={{ textAlign: 'center' }}>
                          We're pitching this date to clients and would like to know if you can make it.
                        </Typography>
                      </Grid>
                    )}

                    {gig.role && (
                      <Grid item xs={12} sm={6}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Role
                          </Typography>
                          <Typography variant="body1">{gig.role}</Typography>
                        </Box>
                      </Grid>
                    )}

                    {gig.rate !== undefined && gig.rate !== null && (
                      <Grid item xs={12} sm={6}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Rate
                          </Typography>
                          <Typography variant="body1">{gig.rate > 0 ? `$${gig.rate.toFixed(2)}` : '-'}</Typography>
                        </Box>
                      </Grid>
                    )}

                    {gig.perDiem !== undefined && gig.perDiem !== null && (
                      <Grid item xs={12} sm={6}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Per Diem
                          </Typography>
                          <Typography variant="body1">
                            {gig.perDiem > 0 ? `$${gig.perDiem.toFixed(2)}` : '-'}
                          </Typography>
                        </Box>
                      </Grid>
                    )}

                    {gig.updatedAt && (
                      <Grid item xs={12} sm={6}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Last Activity
                          </Typography>
                          <Typography variant="body1">
                            {gig.updatedAt instanceof Timestamp
                              ? new Intl.DateTimeFormat('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit'
                                }).format(gig.updatedAt.toDate())
                              : '-'}
                            {gig.updatedBy && ` • ${gig.updatedBy}`}
                          </Typography>
                        </Box>
                      </Grid>
                    )}

                    {gig.notes && (
                      <Grid item xs={12}>
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            Notes
                          </Typography>
                          {typeof gig.notes === 'string' ? (
                            <Box 
                              sx={{ 
                                p: 1.5, 
                                bgcolor: 'action.hover', 
                                borderRadius: 1,
                                borderLeft: 3,
                                borderColor: 'primary.main'
                              }}
                            >
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {gig.notes}
                              </Typography>
                            </Box>
                          ) : Array.isArray(gig.notes) && gig.notes.length > 0 ? (
                            <Stack spacing={1.5}>
                              {gig.notes.map((note, idx) => (
                                <Box 
                                  key={idx} 
                                  sx={{ 
                                    p: 1.5, 
                                    bgcolor: 'action.hover', 
                                    borderRadius: 1,
                                    borderLeft: 3,
                                    borderColor: idx === (gig.notes as any[]).length - 1 ? 'primary.main' : 'divider'
                                  }}
                                >
                                  {(note.timestamp || (note.createdBy && note.createdBy !== 'Unknown')) && (
                                    <Typography 
                                      variant="caption" 
                                      color="text.secondary" 
                                      display="block" 
                                      sx={{ mb: 0.5 }}
                                    >
                                      {note.timestamp 
                                        ? `${note.timestamp instanceof Timestamp 
                                            ? note.timestamp.toDate().toLocaleString() 
                                            : note.timestamp instanceof Date
                                              ? note.timestamp.toLocaleString()
                                              : ''
                                          }${note.createdBy && note.createdBy !== 'Unknown' ? ` • ${note.createdBy}` : ''}`
                                        : note.createdBy
                                      }
                                    </Typography>
                                  )}
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {note.note}
                                  </Typography>
                                </Box>
                              ))}
                            </Stack>
                          ) : null}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                  <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                    <ButtonsSwitch gig={gig} onShowConfirmation={handleShowConfirmation} />
                  </Stack>
                  {showDetails && <GigDetails gig={gig} />}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmationDialog.open}
        onClose={canProceed ? handleConfirmationClose : undefined}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        disableEscapeKeyDown={!canProceed}
        sx={{
          '& .MuiDialog-paper': {
            ...(isMobile
              ? {
                  margin: 0,
                  maxHeight: '100%',
                  height: '100%',
                  borderRadius: 0
                }
              : {
                  margin: '32px',
                  maxHeight: 'calc(100% - 64px)'
                })
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            px: isMobile ? 2 : 3
          }}
        >
          We need your confirmation
          {!canProceed && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Please read all content below to continue
              </Typography>
              <LinearProgress
                variant="determinate"
                value={scrollProgress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 3
                  }
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {Math.round(scrollProgress)}%
                </Typography>
              </Box>
            </Box>
          )}
        </DialogTitle>
        <DialogContent
          ref={contentRef}
          onScroll={handleDialogScroll}
          sx={{
            px: isMobile ? 2 : 3,
            py: isMobile ? 1 : 2,
            flex: 1,
            overflow: 'auto'
          }}
        >
          <Box
            dangerouslySetInnerHTML={{ __html: stripBlackWhiteColors(confirmationDialog.content) }}
            sx={{
              '& p': { mb: 1 },
              '& ul': { pl: 2, mb: 1 },
              '& li': { mb: 0.5 },
              fontSize: isMobile ? '0.875rem' : '1rem',
              lineHeight: 1.4
            }}
          />
        </DialogContent>
        <DialogActions
          sx={{
            px: isMobile ? 2 : 3,
            pb: isMobile ? 2 : 3,
            pt: isMobile ? 1 : 2,
            gap: 1,
            flexShrink: 0
          }}
        >
          <Button
            onClick={handleConfirmationClose}
            color="primary"
            size={isMobile ? 'small' : 'medium'}
            fullWidth={isMobile}
            disabled={!canProceed}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAvailable}
            color="primary"
            variant="contained"
            size={isMobile ? 'small' : 'medium'}
            fullWidth={isMobile}
            disabled={!canProceed}
          >
            {canProceed ? 'I agree, continue' : 'Please read all content'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TalentView;
